/**
 * OSS 上传分流策略（不依赖 ali-oss，client 由调用方注入，便于单测）。
 *
 * 背景：附件浏览器直传 OSS，小文件走单次 `put` 足够；大文件（视频 / 压缩包）单次 PUT 在慢网下
 * 极易 `ERR_CONNECTION_RESET`（见 bugfix-log 2026-06-11 / risks R13）。大文件改走 `multipartUpload`
 * 分片上传，断连时用 checkpoint 续传重试，并透出进度。
 */

/** 超过该大小走分片上传，否则单次 put。10MB。 */
export const MULTIPART_THRESHOLD = 10 * 1024 * 1024;
/** 分片大小 1MB（ali-oss 最小 100KB）。 */
export const MULTIPART_PART_SIZE = 1 * 1024 * 1024;
/** 并发分片数。 */
export const MULTIPART_PARALLEL = 4;
/** 分片上传断连后的额外重试次数（首试之外）。 */
export const MULTIPART_MAX_RETRY = 2;
/** 单次 put 超时，与历史保持一致。 */
export const PUT_TIMEOUT = 180000;

export interface OssPutResult {
  name: string;
  url?: string;
}

export interface OssLikeClient {
  put(name: string, file: File, opts?: Record<string, unknown>): Promise<OssPutResult>;
  multipartUpload(name: string, file: File, opts?: Record<string, unknown>): Promise<OssPutResult>;
}

export interface PutStrategyOptions {
  /** 进度回调，0~1。小文件成功后直接以 1 调用一次。 */
  onProgress?: (percent: number) => void;
}

/**
 * 按文件大小分流上传：
 * - `size <= MULTIPART_THRESHOLD`：单次 `put`，成功后 `onProgress(1)`。
 * - 否则：`multipartUpload` 分片上传，进度透传；断连失败用上次 checkpoint 续传，最多重试
 *   `MULTIPART_MAX_RETRY` 次，全部失败抛出最后一次错误。
 */
export async function putWithStrategy(
  client: OssLikeClient,
  name: string,
  file: File,
  options: PutStrategyOptions = {},
): Promise<OssPutResult> {
  const { onProgress } = options;
  const size = file?.size ?? 0;

  if (size <= MULTIPART_THRESHOLD) {
    const res = await client.put(name, file, { timeout: PUT_TIMEOUT });
    onProgress?.(1);
    return res;
  }

  let checkpoint: unknown;
  let lastErr: unknown;
  for (let attempt = 0; attempt <= MULTIPART_MAX_RETRY; attempt += 1) {
    try {
      return await client.multipartUpload(name, file, {
        partSize: MULTIPART_PART_SIZE,
        parallel: MULTIPART_PARALLEL,
        checkpoint,
        // ali-oss progress 形如 (p, checkpoint) => void，记录断点供续传
        progress: (percent: number, cpt: unknown) => {
          checkpoint = cpt;
          onProgress?.(percent);
        },
      });
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr;
}
